import React, {useCallback, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAppDispatch} from 'store/customer';
import {
    selectedMenuSet,
    setIsRoomPlannerEditing,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {MenuItem} from 'components/customer/RoomPlanner/components/Menu/MenuContext';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import FloatingMenuButton, {
    FloatingMenuButtonIcon,
} from 'components/customer/RoomPlanner/components/common/FloatingMenuButton';
import {
    PLACEHOLDER_EDIT_PARAM,
    PLACEHOLDER_FACE_PARAM,
    PLACEHOLDER_NAME_PARAM,
    buildPlaceholderEditorParams,
} from 'components/customer/RoomPlanner/helpers/placeholder/editorParams';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';

export {
    PLACEHOLDER_EDIT_PARAM,
    PLACEHOLDER_FACE_PARAM,
    PLACEHOLDER_NAME_PARAM,
    buildPlaceholderEditorParams,
};

/**
 * Opens the appliance editor without handleClick — that path deselects the mesh.
 * @return {Function} Click handler that opens the placeholder editor
 */
export const useOpenPlaceholderEditor = () => {
    const {selectedCabinetGroup} = useRoomPlannerContext();
    const isRoomPreview = useRoomPreview();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    return useCallback(() => {
        if (isRoomPreview || !selectedCabinetGroup?.userData?.isPlaceholder) {
            return;
        }

        dispatch(selectedMenuSet(MenuItem.ADD_APPLIANCE));
        setSearchParams(
            buildPlaceholderEditorParams(searchParams, selectedCabinetGroup)
        );
        dispatch(setIsRoomPlannerEditing(true));
    }, [
        isRoomPreview,
        selectedCabinetGroup,
        searchParams,
        setSearchParams,
        dispatch,
    ]);
};

/**
 * Floating-menu Edit button that opens placeholder specs in the appliance panel.
 * @return {JSX.Element}
 */
const PlaceholderEdit = () => {
    const handleEditClick = useOpenPlaceholderEditor();
    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const {setEditModeHandler} = useRoomPlannerContext();

    useEffect(() => {
        if (setEditModeHandler) {
            setEditModeHandler(handleEditClick);
        }
        return () => setEditModeHandler?.(null);
    }, [handleEditClick, setEditModeHandler]);

    return (
        <OverlayTrigger placement="top" overlay={<strong>Edit (E)</strong>}>
            <FloatingMenuButton onClick={handleEditClick}>
                <FloatingMenuButtonIcon
                    className="icon_button"
                    color={
                        isMobileLandscape || isPortraitDevices
                            ? undefined
                            : '#b2bec3'
                    }
                    iconName="Button-Edit-Primary.svg"
                />
                <label>Edit</label>
            </FloatingMenuButton>
        </OverlayTrigger>
    );
};

export default PlaceholderEdit;
